import React from "react";
import { Box, Flex, Heading, Icon, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { FiBarChart2, FiBookOpen, FiFileText, FiRefreshCw, FiTrendingUp } from "react-icons/fi";
import { useColorModeValue } from "../components/ui/color-mode";

const featureSections = [
  {
    title: "Reverse DCF",
    text: "See the market-implied growth rate and market-implied assumptions embedded in a stock price before deciding whether they are realistic.",
    icon: FiRefreshCw,
  },
  {
    title: "Traditional DCF Calculator",
    text: "Build a discounted cash flow model from revenue, margins, reinvestment, discount rates, terminal value, and intrinsic value per share.",
    icon: FiBarChart2,
  },
  {
    title: "Historical Financials",
    text: "Review income statement, balance sheet, and cash flow history before turning business performance into stock valuation assumptions.",
    icon: FiBookOpen,
  },
  {
    title: "Sensitivity and Charts",
    text: "Compare how valuation changes when growth, margins, discount rates, and terminal assumptions move.",
    icon: FiTrendingUp,
  },
  {
    title: "Filing-Sourced Metrics",
    text: "Use public metrics sourced from company filings to connect SEC disclosures with DCF drivers and operating trends.",
    icon: FiFileText,
  },
];

const HomeFeatureSection = () => {
  const bandBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const linkColor = useColorModeValue("teal.700", "teal.300");

  return (
    <Box as="section" maxW="1180px" mx="auto" px={{ base: 5, md: 8 }} py={{ base: 10, md: 14 }}>
      <VStack align="start" gap={4} mb={7}>
        <Heading as="h2" size="xl">
          Valuation tools for the full DCF workflow
        </Heading>
        <Text color={mutedTextColor} maxW="760px" lineHeight="1.7">
          Move from raw company data to a defensible stock valuation model without losing the
          connection between business performance, market-implied assumptions, and intrinsic value.
        </Text>
      </VStack>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={5}>
        {featureSections.map((feature) => (
          <Box
            key={feature.title}
            bg={bandBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={5}
          >
            <Flex align="center" gap={2} mb={3}>
              {feature.icon && <Icon as={feature.icon} boxSize={5} color={linkColor} aria-hidden="true" />}
              <Heading as="h3" size="md">
                {feature.title}
              </Heading>
            </Flex>
            <Text color={mutedTextColor} lineHeight="1.65">
              {feature.text}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default HomeFeatureSection;
